import React, { useState } from 'react';
import { messagesService } from '../../services/messagesService';
import { ReplyType } from '../../types/messages';
import { notify } from '../../lib/globalNotify';
import { sendToTerminal } from '../../lib/terminalLogger';

const LOG_PREFIX = '[REPLY_SECTION]';

interface ReplySectionProps {
  messageId: number;
  respostaObrigatoria: boolean;
  onReplySent?: (unreadCount?: number) => void;
}

/**
 * Seção para enviar resposta a uma mensagem
 */
export function ReplySection({ messageId, respostaObrigatoria, onReplySent }: ReplySectionProps) {
  const [replyText, setReplyText] = useState('');
  const [replyType, setReplyType] = useState<ReplyType>('acknowledgment');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSendReply = async () => {
    // Validação
    if (!replyText.trim()) {
      if (respostaObrigatoria) {
        setError('Resposta é obrigatória');
        return;
      }
      // Se não é obrigatória, pode enviar vazia (confirmação simples)
    }

    try {
      setIsSending(true);
      setError(null);

      console.log('[ReplySection] Enviando resposta:', {
        messageId,
        tipo: replyType,
        mensagem: replyText.substring(0, 50) + (replyText.length > 50 ? '...' : '')
      });

      const response = await messagesService.sendReply(messageId, {
        mensagem: replyText.trim() || 'Confirmado',
        tipo: replyType
      });

      console.log('[ReplySection] Resposta enviada com sucesso:', {
        messageId,
        replyId: response.data?.id,
        unreadCount: response.data?.unread_count
      });

      notify({
        type: 'success',
        title: 'Resposta enviada',
        message: 'Sua resposta foi enviada com sucesso.'
      });
      
      // ✅ MELHORIA 5: Usar unread_count da resposta
      sendToTerminal(`✅ ${LOG_PREFIX} Resposta enviada`, { 
        messageId,
        tipo: replyType,
        unread_count: response.data?.unread_count
      });
      
      setReplyText('');
      setReplyType('acknowledgment');
      
      // ✅ MELHORIA 5: Passar unread_count para callback
      onReplySent?.(response.data?.unread_count);
    } catch (err) {
      console.error('[ReplySection] Erro ao enviar resposta:', {
        messageId,
        error: err instanceof Error ? err.message : 'Erro desconhecido',
        stack: err instanceof Error ? err.stack : undefined
      });

      const errorMessage = err instanceof Error
        ? err.message
        : 'Não foi possível enviar a resposta. Tente novamente.';

      setError(errorMessage);

      notify({
        type: 'error',
        title: 'Erro ao Enviar Resposta',
        message: errorMessage
      });

      sendToTerminal(`❌ ${LOG_PREFIX} Erro ao enviar resposta`, {
        messageId,
        error: errorMessage
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="mi-reply">
      <textarea
        value={replyText}
        onChange={(e) => {
          setReplyText(e.target.value);
          setError(null);
        }}
        placeholder={respostaObrigatoria ? 'Resposta obrigatória...' : 'Resposta (opcional)...'}
        className="mi-reply-input"
        rows={2}
        disabled={isSending}
      />

      {error && <p className="mi-reply-error">{error}</p>}

      <div className="mi-reply-footer">
        <select
          value={replyType}
          onChange={(e) => setReplyType(e.target.value as ReplyType)}
          className="mi-reply-select"
          disabled={isSending}
        >
          <option value="acknowledgment">Ciência</option>
          <option value="confirmation">Confirmação</option>
          <option value="question">Pergunta</option>
        </select>

        <div className="mi-reply-actions">
          <button
            type="button"
            onClick={() => {
              setReplyText('');
              setError(null);
              onReplySent?.();
            }}
            disabled={isSending}
            className="mi-btn"
          >
            Cancelar
          </button>

          <button
            type="button"
            onClick={handleSendReply}
            disabled={isSending || (respostaObrigatoria && !replyText.trim())}
            className="mi-btn mi-btn-primary"
          >
            {isSending ? 'Enviando...' : 'Enviar'}
          </button>
        </div>
      </div>
    </div>
  );
}

